<template>
  <div :class="bindings.outerClass">
    <RichText v-if="bindings.preContent" :class="bindings.preContentClass" :field="{ value: bindings.preContent }" />
    <FormKit v-bind="extendedBindings" v-model="model" :innerClass="bindings.innerClass || 'input-sm'" @blur="onBlur()">
      <template v-if="bindings.suffixIcon" #suffixIcon="context">
        <RichText :field="{ value: context.suffixIcon ?? '' }" />
      </template>
      <template #prevIcon>
        <BaseIcon iconClass="bg-datepicker-prev-icon" />
      </template>
      <template #nextIcon>
        <BaseIcon iconClass="bg-datepicker-next-icon" />
      </template>
      <template #calendarIcon> <BaseIcon iconClass="bg-datepicker-calendar-icon" /> </template>
    </FormKit>
    <RichText class="formkit-help text-sm text-racq-dark-grey mt-4" :field="{ value: helpTextTokenReplaced }" />
    <RichText
      v-if="bindings.postContent && bindings.showPostContent"
      :class="bindings.postContentClass"
      :field="{ value: bindings.postContent }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { FormKit } from '@formkit/vue';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { format } from 'date-fns';
import type { VueFormInputProps } from '@/composables/useFormInputData';
import BaseIcon from '@/components/BaseIcon/BaseIcon.vue';

const props = defineProps<{
  bindings: VueFormInputProps;
  minDateToday?: boolean;
  maxDateByDays?: number;
  testDate?: Date;
  minDateValidationMessage?: string;
  maxDateValidationMessage?: string;
  timezone?: string;
}>();

let minDate: Date | null = null;
let maxDate: Date | null = null;

if (props.maxDateByDays == 0 || props.maxDateByDays) {
  maxDate = props.testDate ? new Date(props.testDate.getTime()) : new Date();
  maxDate.setDate(maxDate.getDate() + props.maxDateByDays);
}

if (props.minDateToday) {
  minDate = props.testDate ? new Date(props.testDate.getTime()) : new Date();
  minDate.setHours(0, 0, 0, 0);
}

watch(
  () => props.maxDateByDays,
  (newValue) => {
    if (newValue) {
      maxDate = props.testDate ? new Date(props.testDate.getTime()) : new Date();
      maxDate.setDate(maxDate.getDate() + newValue);
    }
  }
);

const model = defineModel<Date | null>({ required: false, default: new Date() });

const extendedBindings = computed(() => {
  const bindings: Record<string, any> = props.bindings;
  if (props.timezone) {
    bindings.timezone = props.timezone;
  }
  if (minDate && maxDate) {
    return {
      ...bindings,
      minDate,
      maxDate,
      validationRules: { requiredCustomValidation, minDateValidation, maxDateValidation },
      validation: [['+requiredCustomValidation'], ['minDateValidation'], ['maxDateValidation']],
      validationMessages: {
        requiredCustomValidation: bindings.validationMessages?.required,
        minDateValidation: props.minDateValidationMessage,
        maxDateValidation: props.maxDateValidationMessage,
      },
    };
  }
  return bindings;
});

const helpTextTokenReplaced = computed(() => {
  if (maxDate) {
    return props.bindings.helpSm?.replace('{maxDate}', format(maxDate, STANDARD_DATE_FORMAT));
  }
  return props.bindings.helpSm;
});

function requiredCustomValidation(node: any) {
  node.store.filter(() => false, 'validation'); // Remove the default datepicker validation message which doesn't meet our requirements
  return !!node.value;
}

function minDateValidation(node: any) {
  node.store.filter(() => false, 'validation');
  return new Date(node.value) >= minDate!;
}

function maxDateValidation(node: any) {
  node.store.filter(() => false, 'validation');
  return new Date(node.value) <= maxDate!;
}

async function onBlur() {
  const elements = document.querySelectorAll('[id*=_popover]');

  if (elements && elements.length > 0) {
    const popover = document.getElementById(elements[0].id);

    if (popover) {
      popover.style.display = 'none';
    }
  }
}
</script>
